/**
 * AdminWiFiProviderSettings — pick & configure the Wi-Fi provider per tenant.
 *
 * Three options exposed (matches backend factory registry):
 *   • mock        — in-memory provider for dev/test (no real network gear).
 *   • radius      — FreeRADIUS / RFC 5176-capable server (works with Zebra
 *                   NX-5500 when the controller delegates auth + CoA to
 *                   an external RADIUS host).
 *   • zebra_wing  — direct SSH/CLI control of a Zebra WiNG controller
 *                   (NX-5500 / RFS / AP / WLC) for local hotspot users.
 *
 * Secrets (`secret`, `password`, `api_token`) are sent in plaintext to the
 * server, encrypted at rest (Fernet) and echoed back as "***SET***". Pasting
 * "***SET***" as a value leaves the existing secret untouched.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Heart, Server, Shield, CheckCircle2, AlertCircle, RefreshCw, Save } from 'lucide-react';
import { getToken } from '@/lib/authToken';
import WiFiRolloutWizard from '../components/WiFiRolloutWizard';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

const PROVIDER_INFO = {
  mock: {
    label: 'Mock (in-memory)',
    blurb: 'Dev/test only — does not touch any real network gear.',
    fields: [],
  },
  radius: {
    label: 'RADIUS (FreeRADIUS / RFC 5176)',
    blurb: 'For Zebra NX-5500 etc. delegating to an external RADIUS server. Supports Access-Request, Disconnect-Message (CoA) and bandwidth CoA via WISPr.',
    fields: [
      { key: 'host',           label: 'RADIUS host', placeholder: 'radius.example.com', required: true },
      { key: 'secret',         label: 'Shared secret', type: 'password', required: true },
      { key: 'auth_port',      label: 'Auth port', defaultValue: 1812, type: 'number' },
      { key: 'acct_port',      label: 'Accounting port', defaultValue: 1813, type: 'number' },
      { key: 'coa_port',       label: 'CoA / DM port', defaultValue: 3799, type: 'number' },
      { key: 'nas_identifier', label: 'NAS-Identifier', defaultValue: 'fwm-portal' },
      { key: 'timeout',        label: 'Timeout (s)', defaultValue: 4, type: 'number' },
    ],
  },
  zebra_wing: {
    label: 'Zebra WiNG (NX-5500 SSH/CLI)',
    blurb: 'SSH into a Zebra WiNG controller and run CLI commands to provision hotspot users + disconnect stations. Works without an external RADIUS server.',
    fields: [
      { key: 'host',         label: 'Controller IP / hostname', placeholder: '10.0.0.1', required: true },
      { key: 'port',         label: 'SSH port', defaultValue: 22, type: 'number' },
      { key: 'username',     label: 'Username', placeholder: 'admin', required: true },
      { key: 'password',     label: 'Password', type: 'password' },
      { key: 'key_path',     label: 'SSH key path (optional)', placeholder: '/etc/secrets/wing_id_rsa' },
      { key: 'known_hosts',  label: 'Known-hosts policy', defaultValue: 'null',
        help: '"null" disables host-key check (staging only). Set to a known_hosts file path in prod.' },
    ],
  },
};

export const AdminWiFiProviderSettings = ({ defaultTenantId = '' }) => {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState(defaultTenantId);
  const [provider, setProvider] = useState('mock');
  const [cfg, setCfg] = useState({});

  const { data: cur, refetch: refetchCur } = useQuery({
    queryKey: ['wifi-provider-config', tenantId],
    queryFn: async () => (await axios.get(
      `${API}/api/wifi/provider/config${tenantId ? `?tenant_id=${tenantId}` : ''}`,
      { headers: authH() })).data,
  });

  const { data: health, refetch: refetchHealth, isFetching: healthLoading } = useQuery({
    queryKey: ['wifi-provider-health', tenantId],
    queryFn: async () => (await axios.get(
      `${API}/api/wifi/provider/health${tenantId ? `?tenant_id=${tenantId}` : ''}`,
      { headers: authH() })).data,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (cur) {
      setProvider(cur.provider || 'mock');
      setCfg(cur.config || {});
    }
  }, [cur]);

  const save = useMutation({
    mutationFn: async () => (await axios.put(`${API}/api/wifi/provider/config`,
      { tenant_id: tenantId || null, provider, config: cfg }, { headers: authH() })).data,
    onSuccess: () => {
      toast.success('Provider config saved');
      qc.invalidateQueries({ queryKey: ['wifi-provider-config'] });
      refetchHealth();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });

  const info = PROVIDER_INFO[provider] || PROVIDER_INFO.mock;
  const HealthIcon = health?.ok ? CheckCircle2 : AlertCircle;
  const healthColor = health?.ok ? 'text-emerald-300' : (health?.configured ? 'text-amber-300' : 'text-slate-500');

  return (
    <div className="space-y-4" data-testid="wifi-provider-settings">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-0 sm:min-w-[220px]">
          <Label className="text-xs text-slate-400">Tenant ID (blank = global env defaults)</Label>
          <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                  className="bg-slate-900/60 border-slate-700 mt-1"
                  data-testid="wifi-provider-tenant" placeholder="Leave empty for global" />
        </div>
        <Button variant="outline" size="sm" onClick={() => { refetchCur(); refetchHealth(); }}>
          <RefreshCw className="h-4 w-4 mr-1" /> Reload
        </Button>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Server className="h-4 w-4 text-indigo-400" /> Provider
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Active provider</Label>
            <Select value={provider} onValueChange={(v) => { setProvider(v); setCfg({}); }}>
              <SelectTrigger className="bg-slate-800/60 border-slate-700 mt-1" data-testid="wifi-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PROVIDER_INFO).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">{info.blurb}</p>
          </div>

          {info.fields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-slate-800">
              {info.fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-400">
                    {f.label} {f.required && <span className="text-rose-400">*</span>}
                  </Label>
                  <Input
                    type={f.type || 'text'}
                    placeholder={f.placeholder || (f.defaultValue !== undefined ? String(f.defaultValue) : '')}
                    value={cfg[f.key] ?? ''}
                    onChange={(e) => setCfg({ ...cfg, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value })}
                    className="bg-slate-800/60 border-slate-700 mt-1"
                    data-testid={`wifi-provider-cfg-${f.key}`}
                  />
                  {f.help && <p className="text-[11px] text-slate-500 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>
          )}

          <div className="pt-3 border-t border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <HealthIcon className={`h-4 w-4 ${healthColor}`} />
              <span className={healthColor}>
                {healthLoading ? 'Probing…' :
                  health?.ok ? `Healthy · ${health.info || ''}` :
                  health?.configured ? `Unreachable: ${health.error || health.info}` :
                  'Not configured'}
              </span>
            </div>
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="wifi-provider-save">
              <Save className="h-4 w-4 mr-1" /> {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/30 border-slate-800/60">
        <CardContent className="pt-4 text-xs text-slate-500 space-y-1">
          <p className="flex items-center gap-1 text-slate-400">
            <Shield className="h-3.5 w-3.5" /> Secrets are Fernet-encrypted before persistence.
          </p>
          <p>Active source: <Badge variant="outline" className="text-slate-300 ml-1">{cur?.source || 'env'}</Badge></p>
          <p>Server-side env override: <code className="text-slate-300">WIFI_PROVIDER</code></p>
        </CardContent>
      </Card>

      <WiFiRolloutWizard tenantId={tenantId} />
    </div>
  );
};

export default AdminWiFiProviderSettings;
